import { useEffect, useRef, useCallback } from 'react';
import { useAuthStore, checkSessionValidity } from '../store/auth';
import { toast } from '../lib/toast';

const SESSION_CHECK_INTERVAL = 10 * 60 * 1000; // Check every 10 minutes
const INACTIVITY_WARNING = 50 * 60 * 1000; // Warn after 50 minutes
const MAX_REFRESH_RETRIES = 3;
const REFRESH_RETRY_DELAYS = [5000, 10000, 20000]; // Exponential backoff

export function useSessionHealthCheck() {
  const { refreshSession, user } = useAuthStore();
  const lastActivityRef = useRef<number>(Date.now());
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const inactivityWarningShownRef = useRef<boolean>(false);
  const refreshRetryCountRef = useRef<number>(0);
  const isRefreshingRef = useRef<boolean>(false);

  // Update last activity timestamp
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    inactivityWarningShownRef.current = false;
    refreshRetryCountRef.current = 0; // Reset retry count on user activity
  }, []);

  // Silent session refresh with retry logic
  const attemptSilentRefresh = useCallback(async (): Promise<boolean> => {
    if (isRefreshingRef.current) {
      return false; // Already refreshing
    }

    isRefreshingRef.current = true;

    try {
      console.log(`Attempting silent session refresh (attempt ${refreshRetryCountRef.current + 1}/${MAX_REFRESH_RETRIES})`);

      const success = await refreshSession();

      if (success) {
        console.log('Session refreshed successfully');
        refreshRetryCountRef.current = 0;
        isRefreshingRef.current = false;
        return true;
      }

      // Refresh failed, try again with backoff
      refreshRetryCountRef.current++;

      if (refreshRetryCountRef.current < MAX_REFRESH_RETRIES) {
        const delay = REFRESH_RETRY_DELAYS[refreshRetryCountRef.current - 1];
        console.log(`Session refresh failed, retrying in ${delay}ms...`);

        await new Promise(resolve => setTimeout(resolve, delay));
        isRefreshingRef.current = false;
        return await attemptSilentRefresh();
      }

      // All retries exhausted
      console.error('Session refresh failed after all retries');
      toast.warning(
        'Unable to refresh your session automatically. Please save your work and sign in again if needed.',
        { duration: 15000 }
      );

      isRefreshingRef.current = false;
      return false;
    } catch (error: any) {
      console.error('Error during session refresh:', error);
      isRefreshingRef.current = false;
      return false;
    }
  }, [refreshSession]);

  // Session health check with silent refresh
  const checkSessionHealth = useCallback(async () => {
    // Skip if not logged in
    if (!user) return true;

    try {
      // Check using cached store function with timeout
      const isValid = await checkSessionValidity();

      if (!isValid) {
        // Attempt silent refresh instead of navigating away
        const refreshed = await attemptSilentRefresh();

        if (!refreshed) {
          // Only show error after all refresh attempts failed
          if (refreshRetryCountRef.current >= MAX_REFRESH_RETRIES) {
            toast.error(
              'Your session could not be refreshed. Please sign in again.',
              { duration: 10000 }
            );
          }
        }

        return refreshed;
      }

      // Warn about inactivity
      const timeSinceActivity = Date.now() - lastActivityRef.current;
      if (
        timeSinceActivity > INACTIVITY_WARNING &&
        !inactivityWarningShownRef.current
      ) {
        inactivityWarningShownRef.current = true;
        toast.warning(
          'You have been inactive for a while. Your session may expire soon.',
          { duration: 10000 }
        );
      }

      return true;
    } catch (error: any) {
      console.error('Error checking session health:', error);
      // On error, attempt silent refresh instead of navigation
      return await attemptSilentRefresh();
    }
  }, [attemptSilentRefresh, user]);

  // Periodic health check
  useEffect(() => {
    if (!user) return;

    checkSessionHealth();
    checkIntervalRef.current = setInterval(checkSessionHealth, SESSION_CHECK_INTERVAL);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [checkSessionHealth, user]);

  // Track user activity
  useEffect(() => {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

    events.forEach(event => {
      document.addEventListener(event, updateActivity);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
    };
  }, [updateActivity]);

  return {
    checkSessionHealth,
  };
}
